/* ============================================================
   js/config.js  —  ChaosStudio Global Configuration
   ============================================================
   ARCHITECTURE LAYER: Config / Feature Flags
   
   PURPOSE:
   - Single source of truth for all feature flags
   - Allows features to be toggled without touching HTML/logic
   - Controls high-demand mode, beta features, caching TTLs
   - All major features check this file before executing
   
   HOW TO USE:
   - import { FLAGS, CACHE_CONFIG, QUEUE_CONFIG } from './config.js'
   - Check FLAGS.AI_GENERATION before running AI calls
   - Toggle HIGH_DEMAND_MODE to shed load instantly
   
   ROLLBACK SAFETY:
   - Set any flag to false to disable a feature site-wide
   - HIGH_DEMAND_MODE = true sheds expensive features in one line
   ============================================================ */

/* ──────────────────────────────────────────────────────────
   A. FEATURE FLAGS
   Set false to disable any feature without breaking the site
   ────────────────────────────────────────────────────────── */
export const FLAGS = {

  // Core features — keep these true in production
  BATTLES:           true,   // Design battle voting pages
  CART:              true,   // Cart and checkout flow
  AUTH:              true,   // Login / sign-in system

  // AI generation — disable to pause under heavy load
  AI_GENERATION:     true,   // Main AI studio generation
  AI_MOCKUP:         true,   // Shirt mockup composite rendering

  // Beta / experimental
  MANUAL_DESIGNER:   true,   // Manual designer beta (/designer-beta)
  SHARE_CARD:        true,   // Social share card generation

  // High-demand / traffic management
  // ⚡ Set HIGH_DEMAND_MODE = true to instantly:
  //   - pause AI generation queue
  //   - show waitlist form instead of generate button
  //   - keep static pages, battles, and browsing fully operational
  HIGH_DEMAND_MODE:  false,

  // Maintenance — hides features with a friendly notice
  MAINTENANCE_MODE:  false,

  // ─── Growth Features (v2.1) ───────────────────────────────
  // Feature 1: Design Battles — themed weekly design competitions
  //   /battles listing, /battles/[id] voting, leaderboard, royalties
  DESIGN_BATTLES:    true,

  // Feature 2: Viral Prompt Share Cards — Instagram reel / story export
  //   "Share My Design" button in studio → 1080×1080 & 1080×1920 cards
  VIRAL_SHARE_CARDS: true,

  // Feature 3: Weekly Drops — limited-time drops with countdown timers
  //   /drops listing, /drops/[id] detail, /drops/archive
  WEEKLY_DROPS:      true,

  // Future-ready placeholders (not yet built)
  CREATOR_EARNINGS:  false,  // Creator royalty / earnings dashboard
  BULK_ORDERS:       false,  // Bulk / B2B ordering
  REFERRAL_SYSTEM:   false,  // Referral codes
};

/* ──────────────────────────────────────────────────────────
   B. SITE METADATA
   ────────────────────────────────────────────────────────── */
export const SITE = {
  name:        'ChaosStudio',
  domain:      'https://chaosstudio.store',
  currency:    'INR',
  symbol:      '₹',
  minPrice:    799,
  email:       'chaoscat@chaostudio.store',
  instagram:   'https://instagram.com/chaosstudio',
};

/* ──────────────────────────────────────────────────────────
   C. QUEUE CONFIG  (used by js/queue.js)
   Controls the AI generation queue behaviour
   ────────────────────────────────────────────────────────── */
export const QUEUE_CONFIG = {
  // Max concurrent jobs running at once (client-side simulation)
  MAX_CONCURRENT:       2,

  // Max jobs waiting in queue before "high demand" UI kicks in
  HIGH_DEMAND_THRESHOLD: 5,

  // Individual job timeout in ms (30 seconds)
  JOB_TIMEOUT_MS:       30_000,

  // How long to wait before auto-retrying a failed job (ms)
  RETRY_DELAY_MS:       3_000,

  // Max number of retries per job
  MAX_RETRIES:          2,

  // Poll interval for queue status updates (ms)
  POLL_INTERVAL_MS:     1_500,

  // Friendly status messages shown to the user
  STATUS_MESSAGES: {
    queued:       '⏳ Your design is in the queue…',
    processing:   '🎨 Generating your design…',
    high_demand:  '🔥 High demand right now — this may take a little longer.',
    finalizing:   '✨ Almost done — finalizing your print…',
    done:         '🎉 Your design is ready!',
    failed:       '❌ Generation failed. Tap retry to try again.',
    timeout:      '⏰ This is taking longer than expected. Retrying…',
  },
};

/* ──────────────────────────────────────────────────────────
   D. CACHE CONFIG  (used by js/cache.js)
   Controls how long data is cached before a fresh fetch
   ────────────────────────────────────────────────────────── */
export const CACHE_CONFIG = {
  // Battle gallery: cache for 30 seconds (read-heavy, can be stale briefly)
  BATTLES_TTL_MS:       30_000,

  // Vote counts: refresh every 8 seconds during active voting
  VOTE_COUNTS_TTL_MS:   8_000,

  // Leaderboard: cache for 60 seconds
  LEADERBOARD_TTL_MS:   60_000,

  // Featured drops on homepage: cache for 5 minutes
  FEATURED_DROPS_TTL_MS: 300_000,

  // Static design gallery: cache for 2 minutes
  GALLERY_TTL_MS:       120_000,

  // User profile: cache for 10 minutes
  USER_PROFILE_TTL_MS:  600_000,

  // Cache storage key prefix
  KEY_PREFIX:           'cs_cache_',
};

/* ──────────────────────────────────────────────────────────
   E. VOTING CONFIG  (used by js/voting.js)
   Anti-spam and throttle settings
   ────────────────────────────────────────────────────────── */
export const VOTE_CONFIG = {
  // Minimum ms between two votes from the same session (throttle)
  VOTE_THROTTLE_MS:     2_000,

  // Max votes per session per battle week (anti-spam)
  MAX_VOTES_PER_SESSION: 50,

  // LocalStorage key for tracking session votes
  SESSION_KEY:          'cs_votes_session',

  // Whether voting requires login (set true to enforce auth gate)
  REQUIRE_AUTH:         false,

  // Optimistic UI — immediately show vote before Firestore confirms
  OPTIMISTIC_UPDATES:   true,
};

/* ──────────────────────────────────────────────────────────
   F. PERFORMANCE CONFIG
   Lazy loading and image optimization settings
   ────────────────────────────────────────────────────────── */
export const PERF_CONFIG = {
  // IntersectionObserver margin — start loading before element enters view
  LAZY_ROOT_MARGIN:     '200px',

  // Image sizes for srcset (responsive images)
  IMG_WIDTHS:           [400, 800, 1200],

  // Page size for paginated battle/gallery lists
  PAGE_SIZE:            12,

  // Debounce delay for search/filter inputs (ms)
  DEBOUNCE_MS:          300,

  // Throttle delay for scroll events (ms)
  SCROLL_THROTTLE_MS:   100,

  // Minimum loading spinner display time — avoids flash (ms)
  MIN_SPINNER_MS:       600,
};

/* ──────────────────────────────────────────────────────────
   G. CDN / ASSET PATHS
   Centralised asset URLs — swap CDN prefix here without editing HTML
   ────────────────────────────────────────────────────────── */
export const ASSETS = {
  // Base path for generated design thumbnails
  DESIGNS_BASE:   '/images/designs/',

  // Base path for shirt mockup backgrounds
  MOCKUPS_BASE:   '/images/mockups/',

  // Fallback placeholder shown while images load
  PLACEHOLDER:    '/images/placeholder.svg',

  // OG image for social sharing
  OG_IMAGE:       '/images/og-home.jpg',
};

/* ──────────────────────────────────────────────────────────
   H. HIGH-DEMAND MODE HELPERS
   Call these when FLAGS.HIGH_DEMAND_MODE changes
   ────────────────────────────────────────────────────────── */

/**
 * isGenerationAllowed()
 * Returns true only if AI generation is enabled AND we're not
 * in high-demand or maintenance mode.
 */
export function isGenerationAllowed() {
  return FLAGS.AI_GENERATION &&
         !FLAGS.HIGH_DEMAND_MODE &&
         !FLAGS.MAINTENANCE_MODE;
}

/**
 * isBattlesAllowed()
 * Battles remain available even in high-demand mode.
 */
export function isBattlesAllowed() {
  return FLAGS.BATTLES && !FLAGS.MAINTENANCE_MODE;
}

/**
 * isManualDesignerAllowed()
 * Manual designer can be independently toggled.
 */
export function isManualDesignerAllowed() {
  return FLAGS.MANUAL_DESIGNER &&
         !FLAGS.HIGH_DEMAND_MODE &&
         !FLAGS.MAINTENANCE_MODE;
}

/* ──────────────────────────────────────────────────────────
   I. ENVIRONMENT DETECTION
   ────────────────────────────────────────────────────────── */
export const ENV = {
  isDev:  location.hostname === 'localhost' || location.hostname === '127.0.0.1',
  isProd: location.hostname.includes('chaosstudio.store'),
  host:   location.origin,
};

/* ──────────────────────────────────────────────────────────
   J. VERSION / BUILD
   Bump version to bust caches on redeploy
   ────────────────────────────────────────────────────────── */
export const BUILD = {
  version:   '2.0.0',
  buildDate: '2026-03-15',
  // Used as query param for cache-busting static assets
  cacheBust: 'v=2',
};

/* ──────────────────────────────────────────────────────────
   K. BATTLES CONFIG  (used by js/battles-engine.js)
   ────────────────────────────────────────────────────────── */
export const BATTLES_CONFIG = {
  // Max submissions per user per battle
  MAX_SUBMISSIONS_PER_USER: 3,
  // Vote refresh polling interval (ms)
  VOTE_POLL_MS: 8_000,
  // Cache TTL for battle listing (ms)
  BATTLE_LIST_TTL_MS: 60_000,
  // Cache TTL for individual battle entries (ms)
  BATTLE_ENTRIES_TTL_MS: 20_000,
  // Entries per page (pagination)
  PAGE_SIZE: 12,
  // Royalty per sale shown to creators (INR)
  DEFAULT_ROYALTY_INR: 50,
};

/* ──────────────────────────────────────────────────────────
   L. WEEKLY DROPS CONFIG  (used by js/drops-engine.js)
   ────────────────────────────────────────────────────────── */
export const DROPS_CONFIG = {
  // Cache TTL for active drop data (ms)
  DROP_TTL_MS: 120_000,
  // Archive page size
  ARCHIVE_PAGE_SIZE: 12,
  // Countdown refresh interval (ms)
  COUNTDOWN_INTERVAL_MS: 1_000,
};

/* ──────────────────────────────────────────────────────────
   M. SHARE CARD CONFIG  (used by js/share-card.js)
   ────────────────────────────────────────────────────────── */
export const SHARE_CONFIG = {
  // Available export sizes: [width, height, label]
  SIZES: [
    [1080, 1080,  'Square (Feed)'],
    [1080, 1920,  'Story / Reel'],
  ],
  // Brand name on card
  BRAND: 'ChaosStudio',
  BRAND_URL: 'chaosstudio.store',
  CAPTION_TEMPLATE: 'Describe your chaos. AI designs your tee. 🔥 @chaosstudio',
};

/* ──────────────────────────────────────────────────────────
   N. FEATURE GUARD HELPERS  (growth features)
   ────────────────────────────────────────────────────────── */
export function isDesignBattlesAllowed() {
  return FLAGS.DESIGN_BATTLES && !FLAGS.MAINTENANCE_MODE;
}
export function isViralShareCardsAllowed() {
  return FLAGS.VIRAL_SHARE_CARDS && !FLAGS.MAINTENANCE_MODE;
}
export function isWeeklyDropsAllowed() {
  return FLAGS.WEEKLY_DROPS && !FLAGS.MAINTENANCE_MODE;
}

export default {
  FLAGS, SITE, QUEUE_CONFIG, CACHE_CONFIG, VOTE_CONFIG, PERF_CONFIG,
  ASSETS, ENV, BUILD, BATTLES_CONFIG, DROPS_CONFIG, SHARE_CONFIG,
  isGenerationAllowed, isBattlesAllowed, isManualDesignerAllowed,
  isDesignBattlesAllowed, isViralShareCardsAllowed, isWeeklyDropsAllowed,
};
